import { CalculatorTypeKeyEnums } from "../models/KeysEnum";
import type { SustainabilityCalculatorPanelTypes } from "../../typings/SustainabilityCalculator";

interface GTMArgs {
  event?: string;
  linkText?: string;
  linkURL?: string;
  faqName?: string;
  navName?: string;
  navCategory?: string;
  sliderOrientation?: string;
}

const setDataGTMElementType = (element: HTMLAnchorElement | HTMLSpanElement | HTMLFormElement, elementType: string) => {
  let href = "";
  if ("href" in element) {
    href = element.href;
  }

  switch (elementType) {
    case "email":
      if (href.startsWith("mailto:")) {
        element.setAttribute("data-gtm-element-type", elementType);
      }
      break;
    case "tel":
      if (href.startsWith("tel:")) {
        element.setAttribute("data-gtm-element-type", elementType);
      }
      break;
    case "sms":
      if (href.startsWith("sms:")) {
        element.setAttribute("data-gtm-element-type", elementType);
      }
      break;
    case "adp":
      if (href.includes(".adp.")) {
        element.setAttribute("data-gtm-element-type", elementType);
      }
      break;
    case "header-main-nav-link":
      if (element.closest(".top-links") && element.closest(".top-links").closest("#mainFooter")) {
        element.setAttribute("data-gtm-element-type", "footer-top-link");
      }
      break;
    case "sliderNav":
      element.setAttribute("data-gtm-element-type", "slider-nav");
      break;
    case "request-quote-cta":
      const parts = href.split("/");
      const lastPart = parts[parts.length - 2];
      const currentPath = window.location.pathname;
      const getAQuotePath = "get-a-quote"
      if (lastPart === getAQuotePath && currentPath !== `/${getAQuotePath}/` ) {
        element.setAttribute("data-gtm-element-type", elementType);
      }
      break;
    default:
      return;
  }
};

const handleGTMClick = (element: HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement | HTMLInputElement | HTMLFormElement, event) => {
  const linkText = element.textContent.replace(/\s+/g, " ");
  let args: GTMArgs = {};

  switch (event) {
    case "faqInteraction":
      element.addEventListener("mousedown", function () {
        if (element.hasAttribute("aria-expanded") && element.getAttribute("aria-expanded") === "false") {
          args = {
            event,
            linkText,
            faqName: element.closest(".faq-accordion").querySelector(".faq-title").textContent,
          };
          window.dataLayer.push(args);
        }
      });
      break;
    case "requestAQuoteCTA":
      const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
      element.addEventListener("mousedown", function () {
        args = {
          event,
          linkText,
          linkURL,
        };

        window.dataLayer.push(args);
      });
      break;
    case "headerNavigation":
      element.addEventListener("mousedown", function () {
        const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
        let navCategory = "";
        if (element.closest(".nav-item")) {
          navCategory = element.closest(".nav-item").querySelector(".nav-link").textContent;
        } else {
          navCategory = element.textContent;
        }

        args = {
          event: "navigation",
          navName: "Main",
          navCategory,
          linkText,
          linkURL,
        };

        window.dataLayer.push(args);
      });
      break;
    case "footerNavigation":
      element.addEventListener("mousedown", function () {
        const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
        args = {
          event: "navigation",
          navName: "Footer nav",
          navCategory: element.closest(".col-md-3").querySelector("h6").textContent,
          linkText,
          linkURL,
        };
        window.dataLayer.push(args);
      });
      break;
    case "sliderNav":
      element.addEventListener("click", function () {
        args.event = event;
        if (element.closest(".swiper-pagination-vertical")) {
          args.sliderOrientation = "Vertical";
        } else {
          args.sliderOrientation = "Horizontal";
        }
        window.dataLayer.push(args);
      });
      break;
    case "serviceFilter":
      element.addEventListener("change", function (evt) {
        const checkbox = evt.target as HTMLInputElement;
        if (checkbox.checked) {
          const filterName = element.closest(".facet").querySelector(".facet__title").textContent.trim();
          const filterValue = element.parentNode.textContent.trim();

          const args = {
            event,
            filterName,
            filterValue,
          };
          window.dataLayer.push(args);
        }
      });
      break;
    case "formStart":
      const formFields = element.querySelectorAll("input, select, textarea");
      let isFormModified = false;
      formFields.forEach((field) => {
        field.addEventListener("focus", () => {
          let formName = null;
          const form = field.closest("form");
          const formTitle = form.closest("form").querySelector("h2.Form__Title");
          if (formTitle) {
            formName = formTitle.textContent.trim();
          } else {
            formName = form.closest("section").querySelector("h2.form-title").textContent.trim();
          }

          if (!isFormModified) {
            const args = {
              event,
              formFieldName: field.parentNode.textContent.trim(),
              formName,
              formID: field.closest("form").id,
            };

            window.dataLayer.push(args);
            isFormModified = true;
          }
        });
      });
      break;
    case "formComplete":
      element.addEventListener("submit", function () {
        let formName = null;
        const formTitle = element.querySelector(".Form__Title");
        if (formTitle) {
          formName = formTitle.textContent.trim();
        } else {
          formName = element.closest("section").querySelector("h2.form-title").textContent.trim();
        }

        const args = {
          event,
          formName,
          formID: element.id,
        };

        window.dataLayer.push(args);
      });
      break;
    case "formFieldEngagement":
      const form = element as HTMLFormElement;
      const formFieldEngage = getFieldsEngagement(form);

      let formName = null;
      const formTitle = form.querySelector(".Form__Title");
      if (formTitle) {
        formName = formTitle.textContent.trim();
      } else {
        formName = form.closest("section").querySelector("h2.form-title").textContent.trim();
      }

      form.addEventListener("submit", function () {
        const args = {
          event,
          formName,
          formID: element.id,
          formFieldEngage: formFieldEngage.map(function (item) {
            return `${Object.keys(item)[0]} - ${Object.values(item)[0]}`;
          }),
        };

        window.dataLayer.push(args);
      });
      break;
    case "episerverFormFieldEngagement":
      handleEpiserverFormFieldEngagementEvent(element as HTMLFormElement);
      break;
    case "episerverFormComplete":
      handleEpiserverFormsGTMSubmitEvent(element as HTMLFormElement);
      break;
    case "clickToCall":
      element.addEventListener("click", function () {
        const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
        const h3Element = element.querySelector("h3");
        let linkText;

        if (h3Element) {
          const aContent = element.cloneNode(true);
          aContent.removeChild(aContent.querySelector("h3"));
          linkText = aContent.textContent.trim();
        } else {
          linkText = element.textContent.trim();
        }

        args = {
          event,
          linkText,
          linkURL,
        };
        window.dataLayer.push(args);
      });
      break;
    case "ctaButton":
      element.addEventListener("mousedown", function () {
        if (
          element.classList.contains("find-service-form__search-btn") ||
          element.classList.contains("btn-location-submit") ||
          element.closest("header") ||
          element.closest("#topUtilityNav") ||
          element.closest("footer")
        )
          return;

        let ctaLocation = "Body";
        const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
        if (element.closest("header")) {
          ctaLocation = "Header";
        } else if (element.closest("footer")) {
          ctaLocation = "Footer";
        }

        const args = {
          event,
          linkURL,
          linkText,
          ctaLocation,
        };

        window.dataLayer.push(args);
      });
      break;
    default:
      element.addEventListener("click", function () {
        const linkURL = new URL(element.getAttribute("href"), window.location.href).href;
        args = {
          event,
          linkText,
          linkURL,
        };
        window.dataLayer.push(args);
      });
  }
};

const handleCalculatorEngagementEvent = (
  typeKey: CalculatorTypeKeyEnums,
  panel: SustainabilityCalculatorPanelTypes,
  serviceType?: string,
  productSelection?: string,
  quantity?: string,
  frequency?: string,
  businessType?: string
) => {
  const defaultArgs = {
    event: "calculatorEngagement",
    engagementType: "Start",
    stepName: "WIN's Curb-to-Grid Process Turns Your Waste into Power",
    stepNumber: "0",
    serviceType: null,
    productSelection: null,
    quantity: null,
    frequency: null,
    businessType: null,
  };

  let args = {};

  switch (typeKey) {
    case CalculatorTypeKeyEnums.OverviewType:
      args = defaultArgs;
      break;
    case CalculatorTypeKeyEnums.ServiceTypeSelectionType:
      args = {
        ...defaultArgs,
        engagementType: "Progress",
        stepName: panel.Title,
        stepNumber: "1",
        serviceType,
      };
      break;
    case CalculatorTypeKeyEnums.ProductSelectionType:
      args = {
        ...defaultArgs,
        engagementType: "Progress",
        stepName: panel.Title,
        stepNumber: "2",
        serviceType,
        productSelection,
      };
      break;
    case CalculatorTypeKeyEnums.ContainerRangeType:
      args = {
        ...defaultArgs,
        engagementType: "Progress",
        stepName: panel.Title,
        stepNumber: "3",
        serviceType,
        productSelection,
        quantity,
      };
      break;
    case CalculatorTypeKeyEnums.FrequencyRangeType:
      args = {
        ...defaultArgs,
        engagementType: "Progress",
        stepName: panel.Title,
        stepNumber: "4",
        serviceType,
        productSelection,
        quantity,
        frequency,
      };
      break;

    case CalculatorTypeKeyEnums.BusinessType:
      args = {
        ...defaultArgs,
        engagementType: "Progress",
        stepName: panel.Title,
        stepNumber: "5",
        serviceType,
        productSelection,
        quantity,
        frequency,
        businessType,
      };
      break;

    default:
      args = {
        ...defaultArgs,
        engagementType: "Complete",
        stepName: panel.Title,
        stepNumber: "6",
        serviceType,
        productSelection,
        quantity,
        frequency,
        businessType,
      };
  }

  window.dataLayer.push(args);
};

const handlePageViewGTMEvent = () => {
  const pageDescriptionScript = document.querySelector('script[type="application/ld+json"]');
  let pageDescription = {};

  if (pageDescriptionScript) {
    pageDescription = JSON.parse(pageDescriptionScript.textContent);
  }

  const pageLocation = window.location.href;

  const pageTemplate = document.querySelector("title")?.textContent;

  const args = {
    event: "pageView",
    pageLocation,
    pageTemplate,
    businessType: pageDescription.business || null,
    productName:
      (pageDescription["@type"] == "Product" || pageDescription["@type"] == "Service") && pageDescription.name ? pageDescription.name : null,
    productCategory:
      (pageDescription["@type"] == "Product" || pageDescription["@type"] == "Service") && pageDescription.category ? pageDescription.category : null,
    industry: pageDescription["@type"] == "Industry" && pageDescription.name ? pageDescription.name : null,
  };

  window.dataLayer.push(args);
};

const handleEpiserverFormsGTMSubmitEvent = (form: HTMLFormElement) => {
  const processedForms = new Set();

  const mutationCallback = (mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        const statusMessage = document.querySelector(".Form__Status__Message");
        if (statusMessage && statusMessage.classList.contains("Form__Success__Message")) {
          const form = statusMessage.closest(".EPiServerForms") as HTMLFormElement;
          const formID = form.id;

          if (!processedForms.has(formID)) {
            let formName = null;
            const formTitle = form.querySelector("h2.Form__Title");
            if (formTitle) {
              formName = formTitle.textContent.trim();
            } else {
              formName = form.closest("section").querySelector("h2.form-title").textContent.trim();
            }

            const args = {
              event: "formComplete",
              formName,
              formID: form.id,
            };

            window.dataLayer.push(args);
            processedForms.add(formID);
          }
        }
      }
    }
  };

  const observer = new MutationObserver(mutationCallback);
  observer.observe(form, { childList: true, subtree: true });
};

const handleEpiserverFormFieldEngagementEvent = (form: HTMLFormElement) => {
  const formFieldEngage = getFieldsEngagement(form);

  const processedForms = new Set();

  const mutationCallback = (mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        const statusMessage = document.querySelector(".Form__Status__Message");
        if (statusMessage && statusMessage.classList.contains("Form__Success__Message")) {
          const form = statusMessage.closest(".EPiServerForms") as HTMLFormElement;
          const formID = form.id;

          if (!processedForms.has(formID)) {
            let formName = null;
            const formTitle = form.querySelector("h2.Form__Title");
            if (formTitle) {
              formName = formTitle.textContent.trim();
            } else {
              formName = form.closest("section").querySelector("h2.form-title").textContent.trim();
            }

            const args = {
              event: "formFieldEngagement",
              formFieldEngage: formFieldEngage.map(function (item) {
                return `${Object.keys(item)[0]} - ${Object.values(item)[0]}`;
              }),
              formName,
              formID: form.id,
            };

            window.dataLayer.push(args);
            processedForms.add(formID);
          }
        }
      }
    }
  };

  const observer = new MutationObserver(mutationCallback);
  observer.observe(form, { childList: true, subtree: true });
};

const getFieldsEngagement = (form: HTMLFormElement) => {
  const formFieldEngage = [];

  for (const field of form.elements) {
    const inputField = field as HTMLInputElement | HTMLTextAreaElement;

    if (!inputField.classList.contains("FormHidden") && (inputField.type === "text" || inputField.tagName === "TEXTAREA")) {
      const labelText = inputField.parentNode.querySelector("label")?.textContent.trim();

      if (labelText) {
        const isLabelUnique = !formFieldEngage.some((item) => Object.keys(item)[0] === labelText);

        if (isLabelUnique) {
          formFieldEngage.push({
            [labelText]: "not-engaged",
          });
        }
      }

      inputField.addEventListener("change", function () {
        if (inputField.value !== "") {
          const labelText = inputField.parentNode.querySelector("label")?.textContent.trim();

          if (labelText) {
            const index = formFieldEngage.findIndex((item) => Object.keys(item)[0] === labelText);

            if (index !== -1) {
              formFieldEngage[index][labelText] = "engaged";
            }
          }
        }
      });
    }
  }

  return formFieldEngage;
};

export interface RequestAQuoteFunnelEventType {
  event: string;
  quoteEngagement: string;
  quoteStep: string;
  quoteStepNumber: string | null;
  state: string | null;
  zip: string | null;
  service: string | null;
  businessType: string | null;
  wasteType: string | null;
  frequency: string | null;
}

const handleRequestAQuoteFunnelEvent = () => {
  const args: RequestAQuoteFunnelEventType = {
    event: "requestAQuote",
    quoteEngagement: "Start",
    quoteStep: "Start a Quote",
    quoteStepNumber: "0",
    state: null,
    zip: null,
    service: null,
    businessType: null,
    wasteType: null,
    frequency: null,
  };

  if (window.location.pathname.includes("/get-a-quote/")) {
    window.dataLayer.push(args);
  }

  document.addEventListener("requestAQuote", function (event: CustomEvent) {
    const { detail } = event;
    window.dataLayer.push(detail);
  });

  document.addEventListener("addAllGTMEvents", function (event: CustomEvent) {
    addAllGTMEvents();
  });
};

const addAllGTMEvents = () => {
  document.querySelectorAll<HTMLAnchorElement>("a").forEach((element) => {
    setDataGTMElementType(element, "email");
    setDataGTMElementType(element, "tel");
    setDataGTMElementType(element, "sms");
    setDataGTMElementType(element, "adp");
    setDataGTMElementType(element, "header-main-nav-link");
    setDataGTMElementType(element, "request-quote-cta");
  });

  document.querySelectorAll<HTMLSpanElement>("span.swiper-pagination-bullet").forEach((element) => setDataGTMElementType(element, "sliderNav"));

  document.querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="sms"]').forEach((element) => handleGTMClick(element, "clickToCall"));

  document.querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="tel"]').forEach((element) => handleGTMClick(element, "clickToCall"));

  document.querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="email"]').forEach((element) => handleGTMClick(element, "emailClick"));

  document.querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="adp"]').forEach((element) => handleGTMClick(element, "adpClick"));

  document
    .querySelectorAll<HTMLButtonElement>('button[data-gtm-element-type="faq-accordion"]')
    .forEach((element) => handleGTMClick(element, "faqInteraction"));

  document
    .querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="header-main-nav-link"]')
    .forEach((element) => handleGTMClick(element, "headerNavigation"));

  document
    .querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="footer-top-link"]')
    .forEach((element) => handleGTMClick(element, "footerNavigation"));

  document.querySelectorAll<HTMLSpanElement>('span[data-gtm-element-type="slider-nav"]').forEach((element) => handleGTMClick(element, "sliderNav"));

  document.querySelectorAll<HTMLFormElement>("form").forEach((element) => handleGTMClick(element, "formStart"));

  document.querySelectorAll<HTMLFormElement>("form").forEach((element) => handleGTMClick(element, "formComplete"));

  document.querySelectorAll<HTMLFormElement>("form:not(.EPiServerForms)").forEach((element) => handleGTMClick(element, "formFieldEngagement"));

  document.querySelectorAll<HTMLFormElement>("form.EPiServerForms").forEach((element) => handleGTMClick(element, "episerverFormComplete"));

  document.querySelectorAll<HTMLFormElement>("form.EPiServerForms").forEach((element) => handleGTMClick(element, "episerverFormFieldEngagement"));

  document
    .querySelectorAll<HTMLAnchorElement>('a[data-gtm-element-type="request-quote-cta"]')
    .forEach((element) => handleGTMClick(element, "requestAQuoteCTA"));

  document.querySelectorAll<HTMLAnchorElement>("a:not([data-gtm-element-type])").forEach((element) => handleGTMClick(element, "ctaButton"));

  document.addEventListener("scheduleLookup", (event: CustomEvent) => {
    const { detail } = event;
    const args = {
      event: "scheduleLookup",
      state: detail.address.countrySubdivision,
      zip: detail.address.postalCode,
      businessType: detail.addressType,
    };

    window.dataLayer.push(args);
  });

  document.addEventListener("calculatorEngagement", (event: CustomEvent) => {
    const { detail } = event;
    if (detail.panel && detail.panel.TypeKey) {
      handleCalculatorEngagementEvent(
        detail.panel.TypeKey,
        detail.panel,
        detail.selectedService,
        detail.productSelection,
        detail.quantity,
        detail.frequency,
        detail.businessType
      );
    }
  });

  handleRequestAQuoteFunnelEvent();

  handlePageViewGTMEvent();
};

document.addEventListener("DOMContentLoaded", addAllGTMEvents);
